import React from "react";

import AppSearchAPIConnector from "@elastic/search-ui-app-search-connector";

import {
  ErrorBoundary,
  Facet,
  SearchProvider,
  SearchBox,
  Results,
  PagingInfo,
  ResultsPerPage,
  Paging,
  Sorting,
  WithSearch,
} from "@elastic/react-search-ui";
import { Layout, MultiCheckboxFacet } from "@elastic/react-search-ui-views";
import "@elastic/react-search-ui-views/lib/styles/styles.css";

import {
  buildAutocompleteQueryConfig,
  buildSearchOptionsFromConfig,
  buildSortOptionsFromConfig,
  getConfig,
} from "../config/inspect-config-helper";

import { BackdropContextConsumer } from "../../layout/Backdrop";

import "../Search.css";
import SearchResult from "./SearchResult";
import SearchResults from "./SearchResults";

const { hostIdentifier, searchKey, endpointBase, engineName } = getConfig();
const connector = new AppSearchAPIConnector({
  searchKey,
  engineName,
  hostIdentifier,
  endpointBase,
});
const config = {
  searchQuery: {
    disjunctiveFacets: ["type", "place"],
    facets: {
      type: { type: "value", size: 10 },
      place: { type: "value", size: 3 },
      topic_high_level: { type: "value", size: 25 },
      topic_detailed: { type: "value", size: 30 },
      regulatory_reference: { type: "value", size: 20 },
    },
    ...buildSearchOptionsFromConfig(),
  },
  autocompleteQuery: buildAutocompleteQueryConfig(),
  apiConnector: connector,
  alwaysSearchOnInitialLoad: true,
};

export default function Search() {
  return (
    <SearchProvider config={config}>
      <BackdropContextConsumer>
        {({ setShow }) => (
          <WithSearch
            mapContextToProps={({ wasSearched, isLoading }) => ({
              wasSearched,
              isLoading,
            })}
          >
            {({ wasSearched, isLoading }) => {
              setShow(isLoading);
              return (
                <div className="App">
                  <ErrorBoundary>
                    <Layout
                      header={<SearchBox autocompleteSuggestions={true} />}
                      sideContent={
                        <div>
                          {wasSearched && (
                            <Sorting
                              label={"Sort by"}
                              sortOptions={buildSortOptionsFromConfig()}
                            />
                          )}
                          <Facet
                            field="type"
                            label="Type"
                            view={MultiCheckboxFacet}
                            filterType="any"
                          />
                          <Facet
                            field="place"
                            label="Place"
                            view={MultiCheckboxFacet}
                            filterType="any"
                          />
                          <Facet
                            field="topic_high_level"
                            label="Topic"
                            view={MultiCheckboxFacet}
                            filterType="all"
                            show={10}
                          />
                          <Facet
                            field="topic_detailed"
                            label="Detailed topic"
                            view={MultiCheckboxFacet}
                            filterType="all"
                            show={10}
                            isFilterable={true}
                          />
                          <Facet
                            field="regulatory_reference"
                            label="Regulatory reference"
                            view={MultiCheckboxFacet}
                            filterType="all"
                            show={10}
                            isFilterable={true}
                          />
                        </div>
                      }
                      bodyContent={
                        <Results
                          titleField={getConfig().titleField}
                          urlField={getConfig().urlField}
                          shouldTrackClickThrough={true}
                          resultView={SearchResult}
                          view={SearchResults}
                        />
                      }
                      bodyHeader={
                        <React.Fragment>
                          {wasSearched && <PagingInfo />}
                          {wasSearched && <ResultsPerPage />}
                        </React.Fragment>
                      }
                      bodyFooter={<Paging />}
                    />
                  </ErrorBoundary>
                </div>
              );
            }}
          </WithSearch>
        )}
      </BackdropContextConsumer>
    </SearchProvider>
  );
}
